import {defineMessages} from 'react-intl';
import {useCallback} from 'react';

import {FullDrawerConfirmation} from '@stripe-internal/conversations-ui';

import {
  useApp,
  useConversation,
  useSetAppState,
  useShowCloseConfirmation,
  useShowRestartConfirmation,
} from '../hooks/appHooks';

const messages = defineMessages({
  title: {
    id: 'conversations_assistant.closeConfirmation.title',
    description: 'Title when user intends to close the chat window',
    defaultMessage: 'This will end your conversation.',
  },
  description: {
    id: 'conversations_assistant.closeConfirmation.description',
    description:
      'Description provided when user intends to close the chat window',
    defaultMessage: 'Are you sure you want to exit?',
  },
  confirm: {
    id: 'conversations_assistant.closeConfirmation.confirm',
    description:
      'Text shown for the close chat button when closing the chat window',
    defaultMessage: 'Yes, end chat',
  },
  cancel: {
    id: 'conversations_assistant.closeConfirmation.cancel',
    description:
      'Text shown for the cancel button when canceling closing the chat window',
    defaultMessage: 'No, keep chatting',
  },
  restartConfirm: {
    id: 'conversations_assistant.restartConfirmation.confirm',
    description: 'Confirm button for starting a new conversation',
    defaultMessage: 'Yes, start new',
  },
});

export function CloseConfirmationModal() {
  const app = useApp();
  const showCloseConfirmation = useShowCloseConfirmation();
  const showRestartConfirmation = useShowRestartConfirmation();
  const setAppState = useSetAppState();
  const {restartConversation} = useConversation();

  // if a custom onClose handler used handles the onClose callback,
  // the default close confirmation behavior will be skipped
  const cancelClose = useCallback(() => {
    setAppState((state) => {
      state.showCloseConfirmation = false;
    });
  }, [setAppState]);

  // if a custom onClose handler used handles the onClose callback,
  // the default close confirmation behavior will be skipped
  const confirmClose = useCallback(() => {
    app.close(true);
  }, [app]);

  const cancelRestart = useCallback(() => {
    setAppState((state) => {
      state.showRestartConfirmation = false;
      // Retain only triggerPoint so that any open intent doesn't persist in
      // state and get re-applied on subsequent visibility changes or conversation
      // starts. The user chose to keep the existing conversation.
      // todo(@danielxu): This block can be removed once we implement a proper
      // intent system, where intents are consumed once and automatically
      // discarded regardless of outcome.
      if (state.triggerParams) {
        state.triggerParams = {triggerPoint: state.triggerParams.triggerPoint};
      }
    });
  }, [setAppState]);

  const confirmRestart = useCallback(() => {
    setAppState((state) => {
      state.showRestartConfirmation = false;
    });
    restartConversation({});
  }, [setAppState, restartConversation]);

  if (showRestartConfirmation) {
    return (
      <FullDrawerConfirmation
        open
        title={messages.title}
        description={messages.description}
        confirmLabel={messages.restartConfirm}
        cancelLabel={messages.cancel}
        onConfirm={confirmRestart}
        onCancel={cancelRestart}
      />
    );
  }

  if (!showCloseConfirmation) {
    return null;
  }

  return (
    <FullDrawerConfirmation
      open
      title={messages.title}
      description={messages.description}
      confirmLabel={messages.confirm}
      cancelLabel={messages.cancel}
      onConfirm={confirmClose}
      onCancel={cancelClose}
    />
  );
}
